import { ChangeEvent, useCallback, useMemo } from 'react';
import { Card, Text } from '@nimbus-ds/components';
import { ShippingRestrictionResponseDto } from '@tiendanube/common';
import { ShippingRestrictionScope } from '@tiendanube/common/src/enums';
import { Stack } from 'commons/components';
import { useModal } from 'commons/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import Skeleton from './Skeleton';
import { type ShippingMethodOption } from '../../../hooks/useShippingMethodsOptions';
import {
  useAutoResetSelection,
  useDisabledShippingMethods,
} from '../../ShippingRestrictionPages/hooks';
import OptionsSection from '../OptionsSection';
import SelectedItemsChipList from '../SelectedItemsChipList';
import {
  GenericSelectionModal,
  ShippingMethodCheckboxItem,
} from '../SelectionModals';
import { RadioOption } from '../shared/types';

interface ShippingMethodsScopeCardProps {
  appName?: string;
  methods: ShippingMethodOption[];
  restrictions: ShippingRestrictionResponseDto[];
  currentRestrictionId?: string;
  value?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  selectedMethods: string[];
  onMethodsChange: (methods: string[]) => void;
}

function ShippingMethodsScopeCard({
  appName,
  methods,
  restrictions,
  currentRestrictionId,
  value = ShippingRestrictionScope.ALL,
  onChange,
  selectedMethods,
  onMethodsChange,
}: Readonly<ShippingMethodsScopeCardProps>): JSX.Element {
  const [showModal, openModal, closeModal] = useModal();

  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.shippingMethodsScope',
  );

  const { isMethodDisabled } = useDisabledShippingMethods({
    restrictions,
    currentRestrictionId,
  });

  const availableMethods = useMemo(
    () => methods.filter((method) => !isMethodDisabled(method)),
    [methods, isMethodDisabled],
  );

  const hasUnavailableMethods = useMemo(
    () => availableMethods.length < methods.length,
    [availableMethods.length, methods.length],
  );

  const getMethodDisabledMessage = useCallback(
    (method: ShippingMethodOption): string | undefined =>
      isMethodDisabled(method) ? t('disabledMessage') : undefined,
    [isMethodDisabled, t],
  );

  const {
    handleSubmit: handleAutoResetSubmit,
    handleRemove: handleAutoResetRemove,
    handleClose: handleAutoResetClose,
  } = useAutoResetSelection({
    fieldName: 'shippingMethodsScope',
    resetValue: ShippingRestrictionScope.ALL,
    onChange,
    onItemsChange: onMethodsChange,
    selectedItems: selectedMethods,
    allItems: methods,
    getItemId: (method) => method.code,
  });

  const allLabel = useMemo(() => {
    if (hasUnavailableMethods) {
      return t(
        `options.allWithCount${
          availableMethods.length > 1 ? 'Multiple' : 'Single'
        }`,
        {
          appName,
          count: availableMethods.length,
        },
      );
    }
    return t('options.all', { appName });
  }, [hasUnavailableMethods, t, appName, availableMethods.length]);

  const options: RadioOption[] = [
    {
      label: allLabel,
      value: ShippingRestrictionScope.ALL,
    },
    {
      label: t('options.specific'),
      value: ShippingRestrictionScope.SPECIFIC,
    },
  ];

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e);

      if (
        e.target.value === ShippingRestrictionScope.SPECIFIC &&
        selectedMethods.length === 0
      ) {
        openModal();
      }
    },
    [onChange, selectedMethods.length, openModal],
  );

  const handleSubmitMethods = useCallback(
    (selectedMethods: string[]) => {
      handleAutoResetSubmit(selectedMethods);
      closeModal();
    },
    [handleAutoResetSubmit, closeModal],
  );

  const handleCloseModal = useCallback(() => {
    handleAutoResetClose();
    closeModal();
  }, [handleAutoResetClose, closeModal]);

  const getMethodName = (code: string | number): string => {
    const method = methods.find((m) => m.code === code.toString());
    return method?.displayName || code.toString();
  };

  return (
    <>
      <Card>
        <Card.Header title={t('title')} />
        <Card.Body>
          <Stack column align="stretch" spacing="tight">
            <Stack.Item>
              <OptionsSection
                name="shippingMethodsScope"
                options={options}
                value={value}
                onChange={handleChange}
              />
            </Stack.Item>
            <Stack.Item>
              <Text>
                {value === ShippingRestrictionScope.ALL
                  ? t('descriptionAll', { appName })
                  : t('descriptionSpecific', { appName })}
              </Text>
            </Stack.Item>
            {value === ShippingRestrictionScope.SPECIFIC &&
              selectedMethods.length > 0 && (
                <Stack.Item>
                  <SelectedItemsChipList
                    items={selectedMethods}
                    editLinkText={t('editLink')}
                    getItemText={getMethodName}
                    onEdit={openModal}
                    onRemove={handleAutoResetRemove}
                  />
                </Stack.Item>
              )}
          </Stack>
        </Card.Body>
      </Card>

      {showModal && (
        <GenericSelectionModal<ShippingMethodOption>
          isOpen
          onClose={handleCloseModal}
          onSubmit={handleSubmitMethods}
          preselected={selectedMethods}
          items={methods}
          getItemId={(method) => method.code}
          getItemSearchText={(method) => method.displayName}
          renderItem={(method, isSelected, onToggle, isDisabled) => (
            <ShippingMethodCheckboxItem
              key={method.code}
              code={method.code}
              displayName={method.displayName}
              checked={isSelected}
              onChange={onToggle}
              disabled={isDisabled}
              disabledMessage={getMethodDisabledMessage(method)}
            />
          )}
          translationPath="deliveryMethods.shippingLimiting.modals.shippingMethods"
          showSelectAll={false}
          getItemDisabled={isMethodDisabled}
          getItemDisabledMessage={getMethodDisabledMessage}
        />
      )}
    </>
  );
}

ShippingMethodsScopeCard.Skeleton = Skeleton;

export default ShippingMethodsScopeCard;
